import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { Alert, Badge, Card, EmptyState, LoadingBlock, PageHeader } from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { useConfig } from "../../lib/config";
import { formatBytes, formatDate } from "../../lib/format";
import type { Page } from "../../lib/types";

export interface DeliveryFileItem {
  id: string;
  name: string;
  size_bytes: number;
  uploaded_at: string;
  downloaded: boolean;
  first_downloaded_at: string | null;
  available: boolean;
  download_count?: number | null;
}

export interface Delivery {
  id: string;
  title: string;
  message: string | null;
  status: "draft" | "released" | "expired" | "cancelled";
  recipient: string;
  released_at: string | null;
  expires_at: string | null;
  available: boolean;
  file_count: number;
  downloaded_count: number;
  files: DeliveryFileItem[];
  pending_uploads: {
    id: string;
    filename: string;
    size: number;
    state: string;
    message: string | null;
  }[];
  /** Email packages (staff view) */
  recipient_email?: string | null;
  recipient_name?: string | null;
  cc_emails?: string[];
  email_subject?: string | null;
}

function daysLeft(iso: string | null) {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));
}

const btn =
  "inline-flex min-h-11 items-center rounded-md bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700";

function PackageCard({
  d,
  tz,
  onDownloaded,
  base = `/api/v1/deliveries/${d.id}`,
  linkTitle = true,
}: {
  d: Delivery;
  tz: string;
  onDownloaded: () => void;
  /** API path of this package (the emailed-link page uses its own). */
  base?: string;
  linkTitle?: boolean;
}) {
  const left = daysLeft(d.expires_at);
  // Download status refreshes after the browser finishes the download.
  const refreshSoon = () => setTimeout(onDownloaded, 3000);
  return (
    <Card>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-bold">
            {linkTitle ? (
              <Link to={`/files/${d.id}`} className="hover:underline">
                {d.title}
              </Link>
            ) : (
              d.title
            )}
          </h2>
          <p className="text-sm text-ink-muted">
            Sent {formatDate(d.released_at, tz)} · available until {formatDate(d.expires_at, tz)}
            {left !== null && left <= 7 && d.available && (
              <Badge tone="warn">
                {left} day{left === 1 ? "" : "s"} left
              </Badge>
            )}
          </p>
          {d.message && <p className="mt-2 whitespace-pre-wrap">{d.message}</p>}
        </div>
        {d.available && d.files.length > 1 && (
          <a className={btn} href={`${base}/download.zip`} onClick={refreshSoon}>
            Download all (ZIP)
          </a>
        )}
      </div>
      {!d.available && <Alert tone="info">These files have expired and were removed.</Alert>}
      <ul className="mt-3 divide-y divide-line">
        {d.files.map((f) => (
          <li
            key={f.id}
            className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <span>
              <span className="font-medium">{f.name}</span>{" "}
              <span className="text-sm text-ink-muted">
                {formatBytes(f.size_bytes)} · added {formatDate(f.uploaded_at, tz)}
              </span>{" "}
              {f.downloaded ? (
                <Badge tone="ok">Downloaded</Badge>
              ) : (
                <Badge tone="warn">Not downloaded yet</Badge>
              )}
            </span>
            {f.available && (
              <a
                className="text-brand-700 underline"
                href={`${base}/files/${f.id}/download`}
                onClick={refreshSoon}
                aria-label={`Download ${f.name}`}
              >
                Download
              </a>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function FilesPage() {
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const qc = useQueryClient();
  const [showExpired, setShowExpired] = useState(false);
  const q = useQuery({
    queryKey: ["deliveries", showExpired],
    queryFn: () => api.get<Page<Delivery>>(`/deliveries?include_expired=${showExpired}`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["deliveries"] });
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Files FROM Bill's"
        description={`Files Bill's sends you are kept for ${config?.delivery_retention_days ?? 45} days, then removed.`}
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-5 w-5"
          checked={showExpired}
          onChange={(e) => setShowExpired(e.target.checked)}
        />
        Show expired packages
      </label>
      {q.isLoading && <LoadingBlock />}
      {q.error && <Alert tone="error">{errorMessage(q.error)}</Alert>}
      {q.data && q.data.items.length === 0 && (
        <EmptyState title="No files right now">
          When Bill's sends you files, you will get an email and they will appear here.
        </EmptyState>
      )}
      {q.data?.items.map((d) => (
        <PackageCard key={d.id} d={d} tz={tz} onDownloaded={refresh} />
      ))}
    </div>
  );
}

export function FilePackagePage() {
  const { id } = useParams();
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["delivery", id],
    queryFn: () => api.get<Delivery>(`/deliveries/${id}`),
  });
  if (q.isLoading) return <LoadingBlock />;
  if (!q.data) return <Alert tone="error">{errorMessage(q.error)}</Alert>;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Files FROM Bill's"
        description={
          <Link className="text-brand-700 underline" to="/files">
            All packages
          </Link>
        }
      />
      <PackageCard
        d={q.data}
        tz={tz}
        onDownloaded={() => qc.invalidateQueries({ queryKey: ["delivery", id] })}
      />
    </div>
  );
}

/** Files opened from an emailed download link (no sign-in needed). */
export function EmailedFilesPage() {
  const { token } = useParams();
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["emailed-files", token],
    queryFn: () => api.get<Delivery>(`/public/files/${token}`),
    retry: false,
  });
  if (q.isLoading) return <LoadingBlock />;
  if (!q.data)
    return (
      <div className="mx-auto max-w-xl">
        <PageHeader title="Files FROM Bill's" />
        <Alert tone="error">{errorMessage(q.error)}</Alert>
      </div>
    );
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <PageHeader
        title="Files FROM Bill's"
        description="Download your files below. No account is needed."
      />
      <PackageCard
        d={q.data}
        tz={tz}
        base={`/api/v1/public/files/${token}`}
        linkTitle={false}
        onDownloaded={() => qc.invalidateQueries({ queryKey: ["emailed-files", token] })}
      />
    </div>
  );
}

/** Small dashboard summary of available packages. */
export function FilesSummary() {
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const q = useQuery({
    queryKey: ["deliveries", false],
    queryFn: () => api.get<Page<Delivery>>("/deliveries"),
  });
  if (q.isLoading) return <LoadingBlock />;
  if (!q.data || q.data.items.length === 0)
    return <p className="text-ink-muted">No files waiting for you.</p>;
  return (
    <ul className="divide-y divide-line">
      {q.data.items.slice(0, 5).map((d) => (
        <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
          <Link to={`/files/${d.id}`} className="font-semibold text-brand-700 underline">
            {d.title}
          </Link>
          <span className="text-sm text-ink-muted">
            {d.downloaded_count} of {d.files.length} downloaded · until{" "}
            {formatDate(d.expires_at, tz)}
          </span>
        </li>
      ))}
    </ul>
  );
}
